import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useRef,
  MouseEvent,
  useMemo,
} from "react";
import { Menu, MenuItem, Divider, ListItemIcon, ListItemText, ListSubheader } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import SnTab from "@shared/components/SnTab";
import SnNavBtn from "@shared/components/SnNavBtn";
import SnFormControls from "@shared/components/SnFormControls";
import {
  SnSortableFormArea,
  SnSortableFormControl,
  sortIdOf,
} from "@shared/components/SnSortableFormControls";
import { SnControlSelector } from "@shared/components/SnControlSelector";
import { showPopupModal } from "@widgets/CommonModal";
import { global, menuChangeStore } from "@shared/libs/Global";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { showDialog } from "@widgets/CommonDialog";
import SnTabHeader from "@shared/components/SnTabHeader";
import { FN_Fi_AutoSlip } from "@shared/components/GridV2/SnMenuItems";

interface SnFormProps {
  data: any;
  controls: any[];
  etcProps?: SnFormEtcProps;
  events?: SnFormEvents;
  header?: React.ReactNode;
  gridref?: any;
  popupRef?: any;
  headerInfo?: { controls: any[]; data: any };
}

export type SnFormRef = {
  applyPopupConditions: (popupConditions: any) => void;
  addRow: (newData?: Record<string, any>) => void;
  getChange: () => {
    modifiedRowcount: number;
    insert: any[];
    update: any[];
    delete: any[];
  };
  resetUpdate: (isRecovery?: boolean) => void;
  deleteForm: () => void;
  checkDataExist: () => boolean;
  requiredValuesCheck: () => boolean;
  getFormdata: () => any;
  setDataValue: (param: string, data: string, source?: string) => Promise<void>;
  setFocus: (param: string) => void;
  getIsReadOnly: (param: string) => boolean;
};

type SnFormEvents = {
  onBtnEtcClick?: (sender: string) => void;
  onBtnAddClick?: (sender: string) => void;
  onBtnDeleteClick?: (sender: string) => void;
  onValueChanged?: (event: ValueChangedEvent) => Record<string, any> | void;
  onEnter?: (event: string, value: string) => Promise<void> | void;
  onControlFocus?: (param: string) => void;
  onHeaderValueChanged?: (
    event: ValueChangedEvent
  ) => Record<string, any> | void;
  onContextMenu?: (params: {
    id: string;
    menu: string;
    functionId: string;
  }) => void;
};

type SnFormEtcProps = {
  gridref?: any;
  title?: string;
  popupRef?: any;
  gridAlign?: "start" | "end"; // 기본값 end (오른쪽 정렬)
  allowXScroll?: boolean;
  labelSize?: number;
  autoFocus?: boolean; // 데이터 변경 시 첫 컨트롤 자동 포커스 여부 (default: true)
};

export interface ValueChangedEvent {
  newValue: any;
  oldValue: any;
  colId: string;
  rowData: any;
  popupRow?: any;
}

type InputEls = {
  el: HTMLElement | null;
  isFocusable: boolean;
};

/** 우클릭 메뉴 제목 줄 — 작고 옅게 (2026-10-02, SearchPanel 과 같다) */
const menuHeadSx = { lineHeight: "28px", fontSize: 12, color: "#7d7e80", bgcolor: "transparent" };

const SnForm = forwardRef<SnFormRef, SnFormProps>(
  (
    {
      data,
      controls = [], // 설정이 없는 화면도 있으므로 기본값 필수(없으면 effect에서 앱 전체가 죽는다)
      etcProps,
      events,
      header,
      gridref,
      popupRef,
      headerInfo,
    }: SnFormProps,
    ref
  ) => {
    const [formData, setFormData] = useState<any>(data);
    const [visibleControls, setVisibleControls] = useState<any[]>([]);
    const visibleControlsRef = useRef<any[]>([]);
    const [hiddenControls, setHiddenControls] = useState<any[]>([]);
    const [editCon, setEditCon] = useState<any>();
    const [requireds, setRequireds] = useState<any>({});
    const [groups, setGroups] = useState<any[]>();
    const [filter, setFilter] = useState<any>();
    const formRef = useRef<HTMLDivElement>(null); // form 영역을 참조
    const [menuPosition, setMenuPosition] = useState<{
      mouseX: any;
      mouseY: any;
    } | null>(null);
    const [menuItems, setMenuItems] = useState<
      Array<{ name: string; group: string; item: any }>
    >([]);
    const isAddContext = useRef<boolean>(false);
    const refs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const inputEls = useRef<InputEls[]>([]);
    const [scrollTop, setScrollTop] = useState(0);
    const [popupCons, setPopupCons] = useState<any>({}); /* */
    const { userInfo } = global();
    /* 배치 편집에 들어가기 직전의 칸 목록 — [편집 취소] 때 되돌린다 (2026-10-02) */
    const layoutSnapshotRef = useRef<{ visible: any[]; hidden: any[] } | null>(null);
    const [isDragOn, setIsDragOn] = useState(false);
    const [focus, setFocus] = useState<boolean>();
    const formDataRef = useRef(formData);
    const modifiedRowsRef = useRef({
      added: [] as Record<string, any>[],
      updated: [] as Record<string, any>[],
      deleted: [] as Record<string, any>[],
    });

    const [hasScroll, setHasScroll] = useState(false);
    // etcProps.autoFocus === false 면 데이터 변경 시 첫 컨트롤 자동 포커스를 끈다
    // (여러 폼/그리드가 한 스크롤 컨테이너에 있을 때 포커스가 다른 섹션으로 튀는 것 방지).
    const enableFirstFocus = useRef<boolean>(etcProps?.autoFocus ?? true);

    const fetchControl = async () => {
      try {
        const groupFilters = controls.filter(
          (control) => control.tp_ctrl === "SnGroupLabel"
        );
        setGroups(groupFilters);

        const requiredsFilter = controls.filter(
          (control) => control.yn_required === "Y"
        );
        let tempreq = Object.assign({});
        requiredsFilter.map((req) => {
          tempreq[req.cd_param] = req.nm_label;
        });
        setRequireds(tempreq);
        const visibleFilters = controls.filter(
          (control) => control.fg_visible && control.fg_visible.trim() == "Y"
        );
        setVisibleControls(visibleFilters);
        const hiddenFilters = controls.filter(
          (control) => control.fg_visible && control.fg_visible.trim() != "Y"
        );
        setHiddenControls(hiddenFilters);
      } catch (error: any) {}
    };

    const fetchData = async () => {
      try {
        requestAnimationFrame(() => {
          setChangeCon(data);
          setFormData(data); // 상태 갱신 (폼 렌더링 유발)
          formDataRef.current = data;
          setFocus(true);
        });
      } catch (error: any) {}
    };

    useEffect(() => {
      fetchData();
    }, [data]);

    // 26.09.23 kdy 그리드 연동 폼(gridref)은 신규행(state === "insert")일 때만 첫 컨트롤 자동 포커스.
    // 조회된 행 사이를 이동할 때마다 폼이 포커스를 가져가면 아래로 스크롤해 둔 폼이 맨 위로 튀어 올라간다.
    // 단독 폼(gridref 없음)은 기존대로 데이터 변경 시 포커스. 명시적 setFocus(param) 호출은 영향 없음.
    const isGridLinked = () =>
      !!(etcProps?.gridref?.current || gridref?.current);
    useEffect(() => {
      if (focus && enableFirstFocus.current) {
        requestAnimationFrame(() => {
          const isInsertRow = formDataRef.current?.state === "insert";
          if (!isGridLinked() || isInsertRow) focusInputByIndex(0);
          setFocus(false);
        });
      }
    }, [focus]);

    useEffect(() => {
      fetchControl();

      if (isAddContext.current || controls.length === 0) return;
      isAddContext.current = true;
      const loadContextMenu = async () => {
        const params = {
          queryId: "getContextMeneu",
          id: "CommonMapper",
          params: {
            cd_menu: controls[0]?.cd_menu || "",
            cd_firm: userInfo.cd_firm,
            cd_ctrl: controls[0]?.cd_form || "",
          },
        };
        const data: any = await fnGetDataSet(params);
        if (!data || data.length === 0) {
          setMenuItems([]);
          return;
        }
        // 핸들러를 클로저로 가두면 최초 렌더의 formData를 붙잡아 버리므로 원본 항목만 보관한다
        setMenuItems(
          data[0].map((item: any) => ({
            name: item.nm_contextmenu,
            group: item.no_group,
            item,
          }))
        );
      };
      loadContextMenu();
    }, [controls]);

    useEffect(() => {
      if (groups) {
        const first = groups[0]?.no_line;
        if (first) {
          groupButtonClick(`${first}`);
        }
      }
    }, [groups, refs]);

    useEffect(() => {
      const el = formRef.current;
      if (!el) return;

      const checkScroll = () => {
        setHasScroll(el.scrollHeight > el.clientHeight);
      };

      checkScroll(); // 최초 체크

      // resize / 내용 변경 대응
      const resizeObserver = new ResizeObserver(checkScroll);
      resizeObserver.observe(el);

      return () => resizeObserver.disconnect();
    }, [visibleControls]); // ⭐ grid 내용 바뀌는 값

    useEffect(() => {
      visibleControlsRef.current = visibleControls;
    }, [visibleControls]);

    const onTextValueChanged = useCallback(
      async (param: string, data: string) => {
        const prevValue = formDataRef.current?.[param] ?? "";
        if (prevValue === data) return;

        const baseFormData = { ...formDataRef.current, [param]: data };

        formValueChanged(baseFormData);
        const event: ValueChangedEvent = {
          oldValue: prevValue,
          newValue: data,
          colId: param,
          rowData: baseFormData,
        };
        formDataRef.current = baseFormData;
        const updatedFields = await valueChangedEvent(event);
        const finalFormData = { ...formDataRef.current, ...updatedFields };

        formValueChanged(finalFormData);
      },
      [formData]
    );

    const onNumberValueChanged = useCallback(
      async (param: string, data: number) => {
        const prevValue = formDataRef.current?.[param] ?? 0;
        if (prevValue === data) return;

        const baseFormData = { ...formData, [param]: data };
        formValueChanged(baseFormData);
        const event: ValueChangedEvent = {
          oldValue: prevValue,
          newValue: data,
          colId: param,
          rowData: baseFormData,
        };
        formDataRef.current = baseFormData;
        const updatedFields = await valueChangedEvent(event);
        const finalFormData = { ...formDataRef.current, ...updatedFields };
        formValueChanged(finalFormData);
      },
      [formData]
    );

    const onPeriodValueChanged = useCallback(
      async (param: string, start: string, end: string) => {
        const [startKey, endKey] = param.split(",").map((p) => p.trim());
        const prevStart = formDataRef.current?.[startKey] ?? "";
        const prevEnd = formDataRef.current?.[endKey] ?? "";

        if (prevStart === start && prevEnd === end) return;

        const baseFormData = {
          ...formDataRef.current,
          [startKey]: start,
          [endKey]: end,
        };
        formValueChanged(baseFormData);
        let finalFormData = baseFormData;
        if (prevStart !== start) {
          const event: ValueChangedEvent = {
            oldValue: prevStart,
            newValue: start,
            colId: startKey,
            rowData: baseFormData,
          };
          formDataRef.current = baseFormData;
          const updatedFields = await valueChangedEvent(event);
          finalFormData = { ...formDataRef.current, ...updatedFields };
        }
        if (prevEnd !== end) {
          const event: ValueChangedEvent = {
            oldValue: prevEnd,
            newValue: end,
            colId: endKey,
            rowData: baseFormData,
          };
          formDataRef.current = baseFormData;
          const updatedFields = await valueChangedEvent(event);
          finalFormData = { ...formDataRef.current, ...updatedFields };
        }

        formValueChanged(finalFormData);
      },
      [formData]
    );

    const onCodeValueChanged = useCallback(
      async (
        code: string,
        codeValue: string,
        name: string,
        nameValue: string,
        popupRow?: any
      ) => {
        const prevValue = formDataRef.current?.[code] ?? "";
        if (prevValue === codeValue) return;

        let baseFormData = {
          ...formDataRef.current,
          [code]: codeValue,
          [name]: nameValue,
        };

        formValueChanged(baseFormData);

        const event: ValueChangedEvent = {
          oldValue: prevValue,
          newValue: codeValue,
          colId: code,
          rowData: baseFormData,
          popupRow,
        };
        formDataRef.current = baseFormData;
        const updatedFields = await valueChangedEvent(event);
        const finalFormData = { ...formDataRef.current, ...updatedFields };

        formValueChanged(finalFormData);
      },
      [formData]
    );

    const formValueChanged = (data: any) => {
      formDataRef.current = data;
      setFormData(data);
      setChangeCon(data);

      if (etcProps?.gridref?.current) {
        etcProps.gridref.current.sharedCellValueChanged(data);
      } else if (gridref?.current) {
        gridref.current.sharedCellValueChanged(data);
      } else {
        const added = modifiedRowsRef.current.added.length > 0;
        const updated = modifiedRowsRef.current.updated.length > 0;

        if (added) {
          modifiedRowsRef.current.added[0] = data;
        } else if (updated) {
          modifiedRowsRef.current.updated[0] = data;
        } else {
          modifiedRowsRef.current.updated.push(data);
        }
      }
    };

    const valueChangedEvent = async (event: ValueChangedEvent) => {
      if (events?.onValueChanged) {
        return (await events?.onValueChanged?.(event)) || {};
      }
    };

    const setChangeCon = useCallback(
      (change: any) => {
        const editObj: Record<string, any> = {};
        const filterObj: Record<string, any> = {};
        const visibleObj: Record<string, boolean> = {};
        const editCons = controls.filter(
          (control) =>
            control.dc_editcon &&
            control.dc_editcon.length > 0 &&
            control.dc_editcon.trim() != ""
        );
        // editCon 처리
        if (editCons && change) {
          editCons.forEach((con) => {
            let conData = con.dc_editcon;

            if (change.state === "insert") {
              conData = conData.replace("new", "false");
            } else {
              conData = conData.replace("new", "true");
            }

            Object.keys(change).forEach((key) => {
              conData = conData.replaceAll(key, change[key]);
            });

            editObj[con.cd_param] = conData;
          });
          setEditCon(editObj);
        }
        const filters = controls.filter(
          (control) =>
            control.dc_filter &&
            control.dc_filter.length > 0 &&
            control.dc_filter.trim() != ""
        );
        // filter 처리
        if (filters && change) {
          filters.forEach((fil) => {
            let filData = fil.dc_filter;
            Object.keys(change).forEach((key) => {
              filData = filData.replaceAll(`{${key}}`, change[key]);
            });
            filterObj[fil.cd_param] = filData;
          });
          setFilter(filterObj);
        }

        // visible 처리
        if (visibleControls) {
          visibleControls.forEach((control) => {
            const raw = editObj?.[control.cd_param];
            if (raw && change.hasOwnProperty(control.cd_param)) {
              try {
                visibleObj[control.cd_param] = Boolean(
                  new Function(`return ${raw};`)()
                );
              } catch (e) {
                visibleObj[control.cd_param] = false;
                console.error("eval error in setChangeCon:", raw, e);
              }
            } else {
              visibleObj[control.cd_param] = false;
            }
          });
        }

        // 필요시 visibleObj 저장 또는 상태 처리 가능
      },
      [visibleControls, controls]
    );

    const setReadOnly = useCallback(
      (param: string) => {
        const expr = editCon?.[param];
        if (!expr) return false;

        try {
          return Boolean(new Function(`return (${expr});`)());
        } catch (e) {
          console.error("eval error in setReadOnly:", expr, e);
          return false;
        }
      },
      [editCon]
    );

    useImperativeHandle(ref, () => ({
      applyPopupConditions: (popupConditions: any) =>
        setPopupCons(popupConditions),
      addRow,
      getChange,
      resetUpdate,
      deleteForm,
      checkDataExist,
      requiredValuesCheck,
      getFormdata: () => {
        return formDataRef.current;
      },
      setDataValue,
      setFocus: setFocusByParam,
      getIsReadOnly: getIsReadOnlyByParam,
    }));

    const addRow = (newData?: Record<string, any>) => {
      if (etcProps?.gridref?.current && gridref?.current) return;

      let baseFormData: any = newData || {};

      const finalFormData = {
        ...baseFormData,
        ...newData,
        state: "insert",
      };
      modifiedRowsRef.current.added.push(finalFormData);
      setFormData(finalFormData);
      setChangeCon(finalFormData);
      formDataRef.current = finalFormData;
    };

    const getChange = () => {
      if (etcProps?.gridref?.current && gridref?.current)
        return {
          modifiedRowcount: 0,
          insert: [],
          update: [],
          delete: [],
        };
      return {
        modifiedRowcount:
          modifiedRowsRef.current.added.length +
          modifiedRowsRef.current.updated.length +
          modifiedRowsRef.current.deleted.length,
        insert: modifiedRowsRef.current.added,
        update: modifiedRowsRef.current.updated,
        delete: modifiedRowsRef.current.deleted,
      };
    };

    /* 메뉴 닫기 — 이 입력폼의 수정 건수를 메뉴별로 알린다. 그리드에 묶인 폼은 그리드가 센다(getChange 가 0) (2026-10-16) */
    const getChangeRef = useRef(getChange);
    getChangeRef.current = getChange;
    const changeMenu = controls?.[0]?.cd_menu || "";
    const changeForm = controls?.[0]?.cd_form || "";
    useEffect(() => {
      if (!changeMenu) return;
      const id = `form:${changeForm}`;
      menuChangeStore.getState().register(changeMenu, id, () => getChangeRef.current().modifiedRowcount);
      return () => menuChangeStore.getState().unregister(changeMenu, id);
    }, [changeMenu, changeForm]);

    const resetUpdate = (isRecovery?: boolean) => {
      if (etcProps?.gridref?.current && gridref?.current) return;
      if (isRecovery) {
        const deleted = modifiedRowsRef.current.deleted;
        if (deleted.length > 0) {
          // 컨트롤은 formDataRef 로 렌더링되므로 ref 도 함께 복구
          formDataRef.current = deleted[0];
          setFormData(deleted[0]);
          setChangeCon(deleted[0]);
        }
      }
      if (modifiedRowsRef.current.added.length > 0) {
        const { state, ...rest } = formDataRef.current;
        if (rest) {
          setFormData(rest);
          setChangeCon(rest);
        }
      }
      modifiedRowsRef.current = { added: [], updated: [], deleted: [] };
    };

    const deleteForm = () => {
      if (etcProps?.gridref?.current && gridref?.current) return;
      if (modifiedRowsRef.current.added.length > 0) {
        modifiedRowsRef.current.added = [];
      } else {
        modifiedRowsRef.current.updated = [];
        modifiedRowsRef.current.deleted.push(formData);
      }
      setFormData(null);
      setChangeCon(null);
      formDataRef.current = null;
    };

    const checkDataExist = () => {
      if (etcProps?.gridref?.current && gridref?.current) return false;
      return Object.keys(formData || {}).length > 0;
    };

    const requiredValuesCheck = () => {
      try {
        const requireds = controls.filter((col) => col.yn_required === "Y");

        modifiedRowsRef.current.added.forEach((change) => {
          requireds.forEach((required) => {
            const key = required.cd_param; // 또는 nm_column, 실제 데이터 키에 맞게 조정
            const value = change[key];

            if (required.tp_ctrl === "SnNumberBox") {
              if (value == null || value === "0") {
                showDialog("alter", `${required.nm_label}은(는) 필수값입니다.`);
                throw "finish";
              }
            } else {
              if (value == null || value === "") {
                showDialog("alter", `${required.nm_label}은(는) 필수값입니다.`);
                throw "finish";
              }
            }
          });
        });

        modifiedRowsRef.current.updated.forEach((change) => {
          requireds.forEach((required) => {
            const key = required.cd_param; // 또는 nm_column, 실제 데이터 키에 맞게 조정
            const value = change[key];

            if (required.tp_ctrl === "SnNumberBox") {
              if (value == null || value === "0") {
                showDialog("alter", `${required.nm_label}은(는) 필수값입니다.`);
                throw "finish";
              }
            } else {
              if (value == null || value === "") {
                showDialog("alter", `${required.nm_label}은(는) 필수값입니다.`);
                throw "finish";
              }
            }
          });
        });

        return true;
      } catch (Exception) {
        if (Exception !== "finish") throw Exception;
        else return false;
      }
    };

    const setDataValue = async (
      param: string,
      data: string,
      source?: string
    ) => {
      const prevValue = formDataRef.current?.[param] ?? "";
      if (prevValue === data) return;
      const baseFormData = { ...formDataRef.current, [param]: data };
      formDataRef.current = baseFormData;
      setFormData(baseFormData);
      setChangeCon(baseFormData);

      const event: ValueChangedEvent = {
        oldValue: prevValue,
        newValue: data,
        colId: param,
        rowData: baseFormData,
      };
      let updatedFields;
      if (source !== "noEvent") {
        updatedFields = await valueChangedEvent(event);
      } else {
        updatedFields = {};
      }

      const finalFormData = { ...baseFormData, ...updatedFields };
      // noChange: 값만 반영하고 수정행으로 등록하지 않음 (SnGrid와 동일 규약)
      if (source === "noChange") {
        setFormData(finalFormData);
        setChangeCon(finalFormData);
      } else {
        formValueChanged(finalFormData);
      }
      formDataRef.current = finalFormData;
    };

    const gridContainerStyle = {
      display: "grid",
      gridTemplateColumns: popupRef
        ? "repeat(6, minmax(62px, 1fr))"
        : "repeat(12, minmax(62px, 1fr))",
      gridAutoRows: "32px",
      rowGap: "10px",
      columnGap: "15px",
      padding: "10px",
      overflowY: "auto" as "auto",
      flex: "1 1 auto",
      position: "relative",
    } as const;

    const moveControl = (fromIdx: number, toIdx: number) => {
      setVisibleControls((prev) => {
        const updated = [...prev];
        const [moved] = updated.splice(fromIdx, 1); // 1. 기존 위치에서 제거
        updated.splice(toIdx, 0, moved); // 2. 목표 인덱스에 삽입
        return updated;
      });
    };

    const removeControl = (Idx: number, Id: number) => {
      setVisibleControls((prev) => {
        const updated = [...prev];
        updated.splice(Idx, 1); // 1. 기존 위치에서 제거
        return updated;
      });

      // controls에서 no_line 값이 Id와 같은 control을 찾아 hiddenControls에 추가
      const controlToHide = controls.find((control) => control.no_line === Id);
      if (controlToHide) {
        setHiddenControls((prev) => [...prev, controlToHide]);
      }
    };

    const handleRightClick = (event: MouseEvent) => {
      event.preventDefault(); // 기본 메뉴 비활성화

      if (formRef.current) {
        const panelArea = formRef.current.getBoundingClientRect(); // panel 영역
        const { clientX: x, clientY: y } = event; // 클릭 좌표

        // 클릭 좌표가 panel 영역 내에 있는지 확인
        const isInsidePanel =
          x >= panelArea.left &&
          x <= panelArea.right &&
          y >= panelArea.top &&
          y <= panelArea.bottom;

        if (isInsidePanel) {
          // 폼은 컨트롤이 조밀해 빈 공간이 거의 없으므로 컨트롤 위에서도 메뉴를 연다. 보여 줄 항목이 없으면 띄우지 않는다 (2026-10-02)
          if (canOpenMenu) setMenuPosition({ mouseX: x, mouseY: y });
        } else {
          handleMenuClose(); // panel 외부에서 클릭하면 메뉴 닫기
        }
      }
    };

    const handleMenuClose = () => {
      setMenuPosition(null);
    };

    /* 배치 편집은 SYSTEM 계정만. 편집 중이면 늘 메뉴가 있다 · 화면별 우클릭 항목(sys_contextmenu)이 있으면 연다.
       보여 줄 항목이 없으면(일반 사용자 평소) 메뉴를 띄우지 않는다 (2026-10-02 — 쓰지 않던 Option 1 · 2 · 3 삭제) */
    const canEditLayout = global.getState().userInfo.cd_user === "SYSTEM";
    const canOpenMenu = isDragOn || canEditLayout || menuItems.length > 0;

    // sys_contextmenu에 등록된 항목 클릭 시 실행. 그리드(SnGrid.handleContextMenu)와 동일한 규약
    const handleContextMenu = (params: any) => {
      handleMenuClose();
      if (!params?.cd_contextmenu) return;

      if (params.cd_contextmenu === "common") {
        const cd_function = params.cd_function;
        if (!cd_function) return;

        if ((getChange().modifiedRowcount ?? 0) > 0) {
          showDialog("alter", "수정중인 내용이 있습니다. 저장 후 처리하세요.");
          return;
        }
        if (!checkDataExist()) return;

        // 폼은 행 개념이 없으므로 rowNode 한 건으로 감싸 전달
        const nodes = [{ data: formDataRef.current, setDataValue }];
        switch (cd_function) {
          case "fn_sn_fin_com_autocount_01": // 회계처리
            FN_Fi_AutoSlip("Y", params.cd_menu, nodes);
            break;
          case "fn_sn_fin_com_autocount_del_01": // 회계취소
            FN_Fi_AutoSlip("N", params.cd_menu, nodes);
            break;
          default:
            showDialog("alter", `처리할 수 없는 기능입니다. [${cd_function}]`);
        }
      } else if (events?.onContextMenu) {
        events.onContextMenu({
          id: params.cd_contextmenu,
          menu: params.cd_menu_target,
          functionId: params.cd_function,
        });
      }
    };

    const setFormControl = () => {
      setIsDragOn((prev) => !prev); // 상태로 토글 → 리렌더 보장
      handleMenuClose();
    };

    /* 항목 선택 — 조회조건과 같은 체크 창을 띄운다. 전에는 창 없이 편집 모드만 꺼졌다 (2026-10-06).
       필수 항목은 체크를 풀 수 없다 — X 로 지울 수 없는 것과 같다. 숨기면 입력할 칸이 없어 저장할 수 없다 */
    const addFormControl = () => {
      const Controls = [
        ...visibleControls.map((c) => ({ ...c, fg_visible: "Y" })),
        ...hiddenControls.map((c) => ({ ...c, fg_visible: "N" })),
      ];
      showPopupModal({
        title: "항목 선택",
        okayBtn: false,
        closeBtn: false,
        width: "250px",
        height: "400px",
        children: (
          <SnControlSelector
            initialControls={Controls}
            isLocked={(c) => c.yn_required === "Y"}
            onApply={(visible, hidden) => {
              setVisibleControls(visible || []);
              setHiddenControls(hidden || []);
            }}
          />
        ),
      });
      handleMenuClose();
    };

    const saveFormControl = async () => {
      const hasCdModule = controls.some(
        (control) =>
          control.cd_module !== undefined && control.cd_module !== null
      );
      const isNew = controls.some(
        (control) => control.cd_user !== userInfo?.cd_user
      );

      const mapControl = (control: any, idx: number, visible: boolean) => ({
        cd_firm: control.cd_firm,
        cd_menu: control.cd_menu,
        cd_form: control.cd_form,
        no_line: control.no_line,
        cd_user: userInfo?.cd_user,
        qt_rowspan: control.qt_rowspan,
        qt_colspan: control.qt_colspan,
        fg_visible: visible ? "Y" : "N",
        no_seq: visible ? idx + 1 : visibleControls.length + idx + 1,
        ...(isNew && {
          tp_ctrl: control.tp_ctrl,
          nm_label: control.nm_label,
          cd_value: control.cd_value,
          tp_format: control.tp_format,
          cd_param: control.cd_param,
          cd_clas: control.cd_clas,
          dc_sql: control.dc_sql,
          cd_popup: control.cd_popup,
          yn_required: control.yn_required,
          dc_editcon: control.dc_editcon,
          cd_displaycolumn: control.cd_displaycolumn,
          dc_filter: control.dc_filter,
        }),
        ...(hasCdModule && { cd_module: control.cd_module }),
      });

      const vControls = visibleControls.map((control, idx) =>
        mapControl(control, idx, true)
      );
      const hControls = hiddenControls.map((control, idx) =>
        mapControl(control, idx, false)
      );

      const updatedControls = [...vControls, ...hControls];
      const save = isNew
        ? { insert: updatedControls }
        : { update: updatedControls };

      const SaveData = !hasCdModule
        ? { sys_formctrl: save }
        : { sys_popupctrl: save };

      handleMenuClose();
      /* 저장되면 보통 모드로 돌아간다. 전에는 저장 뒤에도 편집 모드로 남아 [취소]를 한 번 더 눌러야 했다 (2026-10-02) */
      const saved = await fnSave(
        !hasCdModule
          ? "SN_SYS_BAS_REG_006_SAVE_01"
          : "SN_SYS_BAS_REG_008_SAVE_01",
        SaveData
      );
      if (saved !== false) {
        layoutSnapshotRef.current = null;
        setIsDragOn(false);
      }
    };

    const handleMenuRightClick = (event: React.MouseEvent) => {
      event.preventDefault();
      handleMenuClose();
      handleRightClick(event);
    };

    const groupButtonClick = (index: any) => {
      const target = refs.current[index];
      if (target && formRef.current) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }
    };

    const setRef = (key: any, node: HTMLDivElement | null) => {
      if (node) {
        refs.current[key] = node; // key를 기준으로 DOM 요소를 저장
      }
    };

    const setInputRef = (
      el: HTMLElement | null,
      index: number,
      isReadOnly: boolean
    ) => {
      inputEls.current[index] = {
        el,
        isFocusable: !isReadOnly,
      };
    };

    const focusInputByIndex = async (startIndex: number) => {
      let index = startIndex;
      while (index < inputEls.current.length) {
        const input = inputEls.current[index]?.el;
        const isFocusable = inputEls.current[index]?.isFocusable;
        if (input && isFocusable) {
          input.focus();
          break;
        }
        index++;
      }
    };

    const handleMoveFocus = (fromIndex: number, target: string) => {
      if (target === "current") {
        const el = inputEls.current?.[fromIndex].el;
        if (el) {
          el.focus();
        }
      } else if (target === "next") {
        const list = inputEls.current;
        for (let i = fromIndex + 1; i < list.length; i++) {
          const entry = list[i];
          if (entry?.el && entry.isFocusable) {
            entry.el.focus();
            return;
          }
        }
        // 끝까지 없으면 현재 blur
        list[fromIndex]?.el?.blur?.();
      }
    };

    const setFocusByParam = (param: string) => {
      const start = Date.now();
      enableFirstFocus.current = false;

      const timer = setInterval(() => {
        const controls = visibleControlsRef.current;

        // visibleControls가 준비될 때까지 대기
        if (controls.length === 0) {
          if (Date.now() - start > 5000) clearInterval(timer);
          return;
        }

        const idx = controls.findIndex((c) => c.cd_param === param);
        if (idx < 0) {
          if (Date.now() - start > 5000) clearInterval(timer);
          return;
        }

        // 26.06.22 kdy dc_editcon계산하는거 기다리는거 때문에 이런식으로 구현함
        const input = inputEls.current[idx]?.el;
        const isFocusable = inputEls.current[idx]?.isFocusable;

        if (input && isFocusable) {
          input.focus();
          (input as HTMLInputElement).select();
          clearInterval(timer);
          return;
        }

        if (Date.now() - start > 5000) {
          clearInterval(timer);
        }
      }, 100);
    };

    const getIsReadOnlyByParam = (param: string) => {
      const idx = visibleControls.findIndex((c) => c.cd_param === param);
      if (idx < 0) return true;

      const input = inputEls.current[idx]?.el;
      const isFocusable = inputEls.current[idx]?.isFocusable;

      if (input && isFocusable) {
        return false;
      }
      return true;
    };

    const renderSharedContent = () => {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            border: "1px solid rgb(186,191,199)",
          }}
        >
          {hasScroll && (
            <div className={"flex justify-start"}>
              {groups != null &&
                groups.length > 0 &&
                groups.map((group) => {
                  return (
                    <div>
                      <SnNavBtn
                        text={group.nm_label}
                        onClick={() => groupButtonClick(group.no_line)}
                      ></SnNavBtn>
                    </div>
                  );
                })}
            </div>
          )}

          <div
            style={gridContainerStyle}
            onContextMenu={handleRightClick}
            ref={formRef}
            className="h-[1px]"
          >
            {/* 배치 편집 — 끄는 동안은 이웃 칸만 비켜 나고, 순서는 손을 놓을 때 한 번 바뀐다 (2026-10-06, react-dnd → dnd-kit) */}
            {isDragOn ? (
              <SnSortableFormArea
                ids={(visibleControls || []).map(sortIdOf)}
                onMove={moveControl}
              >
                <>
                  {visibleControls != null &&
                    visibleControls.length > 0 &&
                    visibleControls.map((control, index) => (
                      <SnSortableFormControl
                        key={sortIdOf(control)}
                        sortId={sortIdOf(control)}
                        labletype="SnForm"
                        control={control}
                        id={control.no_line}
                        index={index}
                        formData={formData}
                        requireds={requireds}
                        popupCons={popupCons}
                        setRef={setRef}
                        setInputRef={setInputRef}
                        handleMoveFocus={handleMoveFocus}
                        onTextValueChanged={onTextValueChanged}
                        onNumberValueChanged={onNumberValueChanged}
                        onPeriodValueChanged={onPeriodValueChanged}
                        onCodeValueChanged={onCodeValueChanged}
                        setReadOnly={setReadOnly}
                        onButtonClick={(param: string) => {
                          events?.onBtnEtcClick != null &&
                            events?.onBtnEtcClick(param);
                        }}
                        removeControl={removeControl}
                        filter={filter}
                        labelSize={etcProps?.labelSize}
                      />
                    ))}
                </>
              </SnSortableFormArea>
            ) : (
              <>
                {visibleControls != null &&
                  visibleControls.length > 0 &&
                  visibleControls.map((control, index) => (
                    <SnFormControls
                      labletype="SnForm"
                      control={control}
                      index={index}
                      formData={formDataRef.current}
                      requireds={requireds}
                      popupCons={popupCons}
                      setRef={setRef}
                      setInputRef={setInputRef}
                      handleMoveFocus={handleMoveFocus}
                      onTextValueChanged={onTextValueChanged}
                      onNumberValueChanged={onNumberValueChanged}
                      onPeriodValueChanged={onPeriodValueChanged}
                      onCodeValueChanged={onCodeValueChanged}
                      setReadOnly={setReadOnly}
                      onButtonClick={(param: string) => {
                        events?.onBtnEtcClick != null &&
                          events?.onBtnEtcClick(param);
                      }}
                      onEnter={events?.onEnter}
                      onControlFocus={events?.onControlFocus}
                      filter={filter}
                      labelSize={etcProps?.labelSize}
                    />
                  ))}
              </>
            )}
            <Menu
              open={menuPosition !== null}
              onClose={handleMenuClose}
              onContextMenu={handleMenuRightClick}
              anchorReference="anchorPosition"
              anchorPosition={
                menuPosition === null
                  ? undefined
                  : { top: menuPosition.mouseY, left: menuPosition.mouseX }
              }
            >
              {/* 우클릭 메뉴 (2026-10-02) — 색 아이콘 · 제목 줄. 평소 [배치 편집](SYSTEM) + 화면별 항목(sys_contextmenu),
                  편집 중 [항목 선택 · 저장 · 편집 취소]. 동작은 전과 같고 이름 · 모양만 바꿨다 */}
              {!isDragOn && (canEditLayout || menuItems.length > 0) && [
                <ListSubheader key="h" sx={menuHeadSx}>폼</ListSubheader>,
                ...(canEditLayout ? [
                <MenuItem key="edit" dense onClick={() => {
                    layoutSnapshotRef.current = { visible: visibleControls, hidden: hiddenControls };
                    setFormControl();
                  }}>
                  <ListItemIcon><TuneIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText>배치 편집</ListItemText>
                </MenuItem>,
                ] : []),
                ...(canEditLayout && menuItems.length > 0 ? [<Divider key="d0" />] : []),
                ...menuItems.map((menu) => (
                  <MenuItem key={menu.name} dense onClick={() => handleContextMenu(menu.item)}>
                    <ListItemText inset>{menu.name}</ListItemText>
                  </MenuItem>
                )),
              ]}
              {isDragOn && [
                <ListSubheader key="h" sx={menuHeadSx}>폼 편집</ListSubheader>,
                <MenuItem key="pick" dense onClick={() => addFormControl()}>
                  <ListItemIcon><PlaylistAddCheckIcon fontSize="small" color="info" /></ListItemIcon>
                  <ListItemText>항목 선택</ListItemText>
                </MenuItem>,
                <MenuItem key="save" dense onClick={() => saveFormControl()}>
                  <ListItemIcon><SaveIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText>저장</ListItemText>
                </MenuItem>,
                <Divider key="d" />,
                <MenuItem key="cancel" dense onClick={() => {
                    /* 편집 취소 — 배치 편집 전으로 되돌리고 보통 모드로 (2026-10-02) */
                    const snap = layoutSnapshotRef.current;
                    layoutSnapshotRef.current = null;
                    if (snap) {
                      setVisibleControls(snap.visible);
                      setHiddenControls(snap.hidden);
                    }
                    setFormControl();
                  }}>
                  <ListItemIcon><CloseIcon fontSize="small" color="error" /></ListItemIcon>
                  <ListItemText>편집 취소</ListItemText>
                </MenuItem>,
              ]}
            </Menu>
          </div>
        </div>
      );
    };

    return (
      <>
        {etcProps?.title ? (
          <SnTab
            titles={[etcProps?.title]}
            width={"100%"}
            header={[
              <SnTabHeader
                onBtnAddClick={events?.onBtnAddClick}
                onBtnDeleteClick={events?.onBtnDeleteClick}
                body={header}
                headerInfo={headerInfo}
                onHeaderValueChanged={events?.onHeaderValueChanged}
                onBtnEtcClick={events?.onBtnEtcClick}
              />,
            ]}
            body={[renderSharedContent()]}
          />
        ) : (
          renderSharedContent()
        )}
      </>
    );
  }
);
export default React.memo(SnForm);
